import { endOfDay, startOfDay } from 'date-fns';
import type { Message } from '../../store/useChatStore';
import type { Report } from '../../store/useReportStore';
import type { Todo } from '../../store/useTodoStore';
import type { ActivityRecordType } from '../../lib/activityType';
import { normalizeActivityType } from '../../lib/activityType';
import { normalizeMoodKey } from '../../lib/moodOptions';
import {
  canEditDailyMyDiary,
  getMyDiaryLockAt as getMyDiaryLockAtMs,
} from '../../lib/reportDiaryEditPolicy';
import {
  findPreferredReportForWindow,
  hasStoredDiaryText,
} from '../../store/reportRecordResolver';

export interface ActivityDistributionItem {
  type: ActivityRecordType;
  minutes: number;
}

export interface MoodDistributionItem {
  mood: string;
  minutes: number;
}

export function reportHasGeneratedDiary(report: Report | null | undefined): boolean {
  return hasStoredDiaryText(report);
}

export function findDailyReportForDate(reports: Report[], date: number | Date): Report | null {
  return findPreferredReportForWindow(reports, 'daily', date);
}

export function findTodayDailyReport(reports: Report[], now = new Date()): Report | null {
  return findDailyReportForDate(reports, now);
}

interface MoodSnapshot {
  activityMood: Record<string, string | undefined>;
  customMoodLabel: Record<string, string | undefined>;
  customMoodApplied?: Record<string, boolean | undefined>;
}

const CUSTOM_MOOD_LABELS = new Set(['自定义', 'Custom', 'Personalizzato']);

export function getReportRange(report: Report): { start: number; end: number } {
  return {
    start: report.startDate || startOfDay(new Date(report.date)).getTime(),
    end: report.endDate || endOfDay(new Date(report.date)).getTime(),
  };
}

function reportHasDiarySignals(report: Report): boolean {
  const stats = report.stats;
  const recurringTotal = stats?.independentRecurring?.total ?? 0;
  const oneTimeTotal = stats?.oneTimeTasks
    ? stats.oneTimeTasks.high.total + stats.oneTimeTasks.medium.total + stats.oneTimeTasks.low.total
    : 0;
  return Boolean(
    report.userNote?.trim() ||
    report.aiAnalysis?.trim() ||
    report.teaserText?.trim() ||
    (stats?.actionAnalysis?.length ?? 0) > 0 ||
    (stats?.moodDistribution?.length ?? 0) > 0 ||
    (stats?.habitCheckin?.length ?? 0) > 0 ||
    (stats?.goalProgress?.length ?? 0) > 0 ||
    recurringTotal > 0 ||
    oneTimeTotal > 0
  );
}

export function resolveDiaryBookInitialTarget(reports: Report[], now = new Date()): Date | null {
  const todayStartMs = startOfDay(now).getTime();
  const dailyReports = reports.filter(
    (report) => report.type === 'daily' && startOfDay(new Date(report.date)).getTime() <= todayStartMs,
  );
  const meaningfulReports = dailyReports.filter(reportHasDiarySignals);
  const candidates = meaningfulReports.length > 0 ? meaningfulReports : dailyReports;
  if (candidates.length === 0) return null;
  const latest = candidates.reduce((best, current) => (current.date > best.date ? current : best));
  return startOfDay(new Date(latest.date));
}

export function isFutureDiaryDate(value: Date, now = new Date()): boolean {
  return startOfDay(value).getTime() > startOfDay(now).getTime();
}

export function getMyDiaryLockAt(reportDate: number | Date): number {
  return getMyDiaryLockAtMs(reportDate);
}

export function canEditMyDiary(report: Report | null | undefined, now = new Date()): boolean {
  if (!report || report.type !== 'daily') return false;
  return canEditDailyMyDiary(report.date, now);
}

/** Return the best message source for a given report: cached date-specific messages if available, otherwise global messages. */
export function getMessagesForReport(
  globalMessages: Message[],
  dateCache: Record<string, Message[]>,
  report: Report | null,
): Message[] {
  if (!report) return globalMessages;
  const d = new Date(report.date);
  const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const cached = dateCache[dateStr];
  return (cached && cached.length > 0) ? cached : globalMessages;
}

export function getDailyMoodDistribution(
  messages: Message[],
  moodSnapshot: MoodSnapshot,
  report: Report | null
): MoodDistributionItem[] {
  if (!report || report.type !== 'daily') return [];

  const { start, end } = getReportRange(report);
  const moodMinutes: Record<string, number> = {};

  messages
    .filter(
      (m) =>
        m.timestamp >= start &&
        m.timestamp <= end &&
        m.mode === 'record' &&
        !m.isMood &&
        m.duration !== undefined
    )
    .forEach((m) => {
      const baseMood = moodSnapshot.activityMood[m.id];
      const customLabel = moodSnapshot.customMoodLabel[m.id];
      const useCustom = moodSnapshot.customMoodApplied?.[m.id] === true;
      const normalizedCustomLabel = customLabel?.trim() ?? '';
      const mood = useCustom && normalizedCustomLabel && !CUSTOM_MOOD_LABELS.has(normalizedCustomLabel)
        ? normalizedCustomLabel
        : baseMood;
      if (!mood) return;
      const minutes = m.duration || 0;
      moodMinutes[mood] = (moodMinutes[mood] || 0) + minutes;
    });

  return Object.entries(moodMinutes)
    .map(([mood, minutes]) => ({
      mood,
      minutes,
    }))
    .filter((entry) => entry.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
}

export function getDailyActivityDistribution(
  messages: Message[],
  report: Report | null,
): ActivityDistributionItem[] {
  if (!report || report.type !== 'daily') return [];

  const { start, end } = getReportRange(report);
  const typeMinutes: Partial<Record<ActivityRecordType, number>> = {};

  messages
    .filter(
      (m) =>
        m.timestamp >= start &&
        m.timestamp <= end &&
        m.mode === 'record' &&
        !m.isMood &&
        m.duration !== undefined &&
        m.duration > 0
    )
    .forEach((m) => {
      const type = normalizeActivityType(m.activityType, m.content) as ActivityRecordType;
      typeMinutes[type] = (typeMinutes[type] || 0) + (m.duration || 0);
    });

  return (Object.entries(typeMinutes) as [ActivityRecordType, number][])
    .map(([type, minutes]) => ({ type, minutes }))
    .filter((d) => d.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
}

/**
 * Compute activity distribution from pre-filtered messages (no report needed).
 * Pass `nowMs` to enable live duration estimation for the currently active message
 * (mirrors the plant system: counts ongoing activities after ≥15 minutes).
 */
export function computeActivityDistribution(messages: Message[], nowMs?: number): ActivityDistributionItem[] {
  const typeMinutes: Partial<Record<ActivityRecordType, number>> = {};
  messages
    .filter(
      (m) =>
        m.mode === 'record' &&
        !m.isMood &&
        (m.duration !== undefined ? m.duration > 0 : nowMs !== undefined)
    )
    .forEach((m) => {
      const resolved = m.duration !== undefined
        ? m.duration
        : Math.max(0, Math.round((nowMs! - m.timestamp) / (1000 * 60)));
      if (resolved <= 0) return;
      const type = normalizeActivityType(m.activityType, m.content) as ActivityRecordType;
      typeMinutes[type] = (typeMinutes[type] || 0) + resolved;
    });
  return (Object.entries(typeMinutes) as [ActivityRecordType, number][])
    .map(([type, minutes]) => ({ type, minutes }))
    .filter((d) => d.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
}

export interface MoodEnergyPoint {
  timestamp: number;
  energy: number;
  mood: string;
}

const MOOD_ENERGY_SCORE: Record<string, number> = {
  happy: 5, focused: 5, calm: 4, satisfied: 4,
  tired: 2, bored: 2, anxious: 1, down: 1,
};

/** Compute mood distribution from pre-filtered messages (no report needed). */
export function computeMoodDistribution(
  messages: Message[],
  activityMood: Record<string, string>,
): MoodDistributionItem[] {
  const moodMinutes: Record<string, number> = {};
  messages
    .filter(m => m.mode === 'record' && !m.isMood && m.duration !== undefined)
    .forEach(m => {
      const mood = activityMood[m.id];
      if (!mood) return;
      moodMinutes[mood] = (moodMinutes[mood] || 0) + (m.duration || 0);
    });
  return Object.entries(moodMinutes).map(([mood, minutes]) => ({ mood, minutes }));
}

/** Compute chronological mood energy points for a timeline chart. */
export function computeMoodEnergyTimeline(
  messages: Message[],
  activityMood: Record<string, string>,
): MoodEnergyPoint[] {
  return messages
    .filter(m => m.mode === 'record' && !m.isMood && activityMood[m.id])
    .map(m => {
      const key = normalizeMoodKey(activityMood[m.id]) ?? 'calm';
      return { timestamp: m.timestamp, energy: MOOD_ENERGY_SCORE[key] ?? 3, mood: activityMood[m.id] };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
}

export function getReportTodosInRange(todos: Todo[], report: Report): Todo[] {
  const { start, end } = getReportRange(report);
  return todos.filter((todo) => {
    const due = todo.dueAt ?? todo.createdAt;
    return due >= start && due <= end;
  });
}
